import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import {
  TrendingUp,
  DollarSign,
  Calendar,
  BarChart3,
  ArrowUpRight,
  ArrowDownLeft,
  Download,
  Wallet,
} from "lucide-react";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { format, eachMonthOfInterval } from "date-fns";

// Empty data generators for coming soon
const generateMonthlyEarnings = (year) => {
  const months = eachMonthOfInterval({
    start: new Date(year, 0, 1),
    end: new Date(year, 11, 31),
  });

  return months.map((month) => ({
    month: format(month, "MMM"),
    fullMonth: format(month, "MMMM yyyy"),
    earnings: 0,
    commissions: 0,
    bonuses: 0,
  }));
};

const generateWeeklyData = () => {
  return [];
};

const generatePayoutData = () => [];

const EarningsCard = ({ title, amount, change, changeType, icon: Icon, color, period }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    whileHover={{ y: -5, scale: 1.02 }}
    transition={{ duration: 0.3 }}
    className="overflow-hidden relative p-6 bg-white rounded-xl border border-gray-200 shadow-sm"
  >
    {/* Background Gradient */}
    <div className={`absolute top-0 right-0 -mt-16 -mr-16 w-32 h-32 rounded-full opacity-5 ${color}`} />
    
    <div className="flex justify-between items-start">
      <div>
        <p className="mb-1 text-sm font-medium text-gray-600">{title}</p>
        <h3 className="mb-2 text-3xl font-bold text-gray-900">₵{amount.toLocaleString()}</h3>
        <div className="flex items-center space-x-2">
          <div className={`flex items-center space-x-1 ${
            changeType === 'increase' ? 'text-green-600' : 'text-red-600'
          }`}>
            {changeType === 'increase' ? (
              <ArrowUpRight className="w-4 h-4" />
            ) : (
              <ArrowDownLeft className="w-4 h-4" />
            )}
            <span className="text-sm font-medium">{change}%</span>
          </div>
          <span className="text-sm text-gray-500">vs {period}</span>
        </div>
      </div>
      <div className={`p-3 bg-opacity-10 rounded-lg ${color}`}>
        <Icon className={`w-6 h-6 ${color.replace('bg-', 'text-')}`} />
      </div>
    </div>
  </motion.div>
);

const PayoutCard = ({ payout }) => (
  <motion.div
    initial={{ opacity: 0, x: -20 }}
    animate={{ opacity: 1, x: 0 }}
    whileHover={{ x: 5 }}
    className="p-4 bg-white rounded-lg border border-gray-200 transition-all duration-200 hover:shadow-md"
  >
    <div className="flex justify-between items-center mb-3">
      <div className="flex items-center space-x-3">
        <div className={`w-2 h-2 rounded-full ${
          payout.status === 'processed' ? 'bg-green-500' : 'bg-yellow-500'
        }`} />
        <span className="font-medium text-gray-900">{payout.id}</span>
      </div>
      <div className={`px-2 py-1 rounded-full text-xs font-medium ${
        payout.status === 'processed' 
          ? 'bg-green-100 text-green-800' 
          : 'bg-yellow-100 text-yellow-800'
      }`}>
        {payout.status}
      </div>
    </div>
    
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <span className="text-sm text-gray-600">Customer</span>
        <span className="text-sm font-medium text-gray-900">{payout.customer}</span>
      </div>
      <div className="flex justify-between items-center">
        <span className="text-sm text-gray-600">Policy Type</span>
        <span className="text-sm text-gray-700">{payout.policyType}</span>
      </div>
      <div className="flex justify-between items-center">
        <span className="text-sm text-gray-600">Commission</span>
        <span className="text-sm font-bold text-green-600">₵{payout.commission.toLocaleString()}</span>
      </div>
      <div className="flex justify-between items-center">
        <span className="text-sm text-gray-600">Date</span>
        <span className="text-sm text-gray-700">{format(new Date(payout.date), "MMM dd, yyyy")}</span>
      </div>
    </div>
  </motion.div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-lg">
        <p className="mb-2 font-medium text-gray-900">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: ₵{entry.value.toLocaleString()}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export default function Earnings() {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [chartType, setChartType] = useState("area"); // area, bar, line

  // Generate data
  const monthlyData = useMemo(() => generateMonthlyEarnings(selectedYear), [selectedYear]);
  const weeklyData = useMemo(() => generateWeeklyData(), []);
  const payoutData = useMemo(() => generatePayoutData(), []);

  // Calculate stats - empty data for coming soon
  const stats = useMemo(() => {
    const today = 0;
    const thisWeek = 0;
    const allTime = 0;
    const pendingPayouts = 0;

    return {
      today: { amount: today, change: 0, changeType: 'increase' },
      thisWeek: { amount: thisWeek, change: 0, changeType: 'increase' },
      allTime: { amount: allTime, change: 0, changeType: 'increase' },
      pendingPayouts: { amount: pendingPayouts, change: 0, changeType: 'increase' },
    };
  }, [weeklyData, payoutData]);

  const availableYears = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  const pendingPayouts = payoutData.filter(p => p.status === 'pending');
  const processedPayouts = payoutData.filter(p => p.status === 'processed');

  return (
    <div className="p-6 min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="mx-auto max-w-full">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
            <div className="mb-4 lg:mb-0">
              <h1 className="mb-2 text-3xl font-bold text-gray-900">
                Earnings Dashboard
              </h1>
              <p className="text-gray-600">
                Track your commission earnings, payouts, and financial performance
              </p>
            </div>
            
            <div className="flex items-center space-x-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex items-center px-4 py-2 space-x-2 bg-white rounded-lg border border-gray-200 transition-all duration-200 hover:shadow-md"
              >
                <Download className="w-4 h-4" />
                <span>Export Report</span>
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* Stats Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="grid grid-cols-1 gap-6 mb-8 md:grid-cols-2 lg:grid-cols-4"
        >
          <EarningsCard
            title="Earnings Today"
            amount={stats.today.amount}
            change={stats.today.change}
            changeType={stats.today.changeType}
            icon={DollarSign}
            color="bg-blue-500"
            period="yesterday"
          />
          
          <EarningsCard
            title="Earnings This Week"
            amount={stats.thisWeek.amount}
            change={stats.thisWeek.change}
            changeType={stats.thisWeek.changeType}
            icon={Calendar}
            color="bg-green-500"
            period="last week"
          />
          
          <EarningsCard
            title="All Time Earnings"
            amount={stats.allTime.amount}
            change={stats.allTime.change}
            changeType={stats.allTime.changeType}
            icon={TrendingUp}
            color="bg-purple-500"
            period="last year"
          />
          
          <EarningsCard
            title="Payout Due This Month"
            amount={stats.pendingPayouts.amount}
            change={Math.abs(stats.pendingPayouts.change)}
            changeType={stats.pendingPayouts.changeType}
            icon={Wallet}
            color="bg-orange-500"
            period="last month"
          />
        </motion.div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 gap-6 mb-8 lg:grid-cols-3">
          {/* Monthly Earnings Chart */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm lg:col-span-2"
          >
            <div className="flex flex-col mb-6 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="mb-1 text-lg font-semibold text-gray-900">Monthly Earnings</h3>
                <p className="text-sm text-gray-600">Track your monthly commission performance</p>
              </div>
              
              <div className="flex items-center mt-4 space-x-3 sm:mt-0">
                {/* Chart Type Selector */}
                <div className="flex items-center p-1 space-x-1 bg-gray-100 rounded-lg">
                  {[
                    { type: 'area', icon: BarChart3 },
                    { type: 'bar', icon: BarChart3 },
                    { type: 'line', icon: TrendingUp },
                  ].map(({ type, icon: Icon }) => (
                    <button
                      key={type}
                      onClick={() => setChartType(type)}
                      className={`p-2 rounded-md transition-all duration-200 ${
                        chartType === type 
                          ? 'bg-white shadow-sm text-blue-600' 
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                    </button>
                  ))}
                </div>
                
                {/* Year Selector */}
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  className="px-3 py-2 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {availableYears.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="h-80">
              {chartType === 'area' && (
                <ResponsiveContainer width="100%" height="100%" key={`area-${selectedYear}`}>
                  <AreaChart data={monthlyData}>
                    <defs>
                      <linearGradient id="colorEarnings" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" stroke="#6B7280" fontSize={12} />
                    <YAxis stroke="#6B7280" fontSize={12} />
                    <Tooltip content={<CustomTooltip />} />
                    <Area
                      type="monotone"
                      dataKey="earnings"
                      stroke="#3B82F6"
                      fillOpacity={1}
                      fill="url(#colorEarnings)"
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
              
              {chartType === 'bar' && (
                <ResponsiveContainer width="100%" height="100%" key={`bar-${selectedYear}`}>
                  <BarChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" stroke="#6B7280" fontSize={12} />
                    <YAxis stroke="#6B7280" fontSize={12} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="earnings" fill="#3B82F6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
              
              {chartType === 'line' && (
                <ResponsiveContainer width="100%" height="100%" key={`line-${selectedYear}`}>
                  <LineChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" stroke="#6B7280" fontSize={12} />
                    <YAxis stroke="#6B7280" fontSize={12} />
                    <Tooltip content={<CustomTooltip />} />
                    <Line
                      type="monotone"
                      dataKey="earnings"
                      stroke="#3B82F6"
                      strokeWidth={3}
                      dot={{ fill: '#3B82F6', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6, stroke: '#3B82F6', strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </motion.div>

          {/* Weekly Performance */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm"
          >
            <div className="mb-6">
              <h3 className="mb-1 text-lg font-semibold text-gray-900">This Week</h3>
              <p className="text-sm text-gray-600">Daily earnings breakdown</p>
            </div>

            <div className="h-64">
              {!weeklyData.length ? (
                <div className="flex justify-center items-center h-full">
                  <div className="text-center">
                    <p className="text-gray-400 text-lg">Coming Soon</p>
                    <p className="text-gray-500 text-sm">Weekly earnings data will be available soon</p>
                  </div>
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%" key="weekly-doughnut">
                  <PieChart>
                    <Pie
                      data={weeklyData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ day, percent }) => `${day} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      innerRadius={40}
                      fill="#8884d8"
                      dataKey="earnings"
                      animationBegin={0}
                      animationDuration={800}
                    >
                      {weeklyData.map((entry, index) => (
                        <Cell 
                          key={`cell-${index}`} 
                          fill={entry.color}
                          stroke={entry.isToday ? "#065F46" : "none"}
                          strokeWidth={entry.isToday ? 3 : 0}
                        />
                      ))}
                    </Pie>
                    <Tooltip 
                      content={({ active, payload }) => {
                        if (active && payload && payload.length) {
                          const data = payload[0].payload;
                          return (
                            <div className="p-3 bg-white rounded-lg border border-gray-200 shadow-lg">
                              <div className="flex items-center mb-2">
                                <div 
                                  className="mr-2 w-3 h-3 rounded-full" 
                                  style={{ backgroundColor: data.color }}
                                />
                                <p className="font-medium text-gray-900">
                                  {data.day} {data.isToday && "🎯"}
                                </p>
                              </div>
                              <p className="mb-1 text-sm text-gray-600">{data.date}</p>
                              <p className="text-sm font-semibold" style={{ color: data.color }}>
                                Earnings: ₵{data.earnings.toLocaleString()}
                              </p>
                              {data.isToday && (
                                <p className="mt-1 text-xs font-medium text-green-600">Today's Performance</p>
                              )}
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Weekly Summary Stats */}
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div className="p-3 text-center bg-gray-50 rounded-lg">
                <p className="mb-1 text-xs text-gray-600">Total This Week</p>
                <p className="text-lg font-bold text-gray-900">
                  ₵{weeklyData.reduce((sum, day) => sum + day.earnings, 0).toLocaleString()}
                </p>
              </div>
              <div className="p-3 text-center bg-blue-50 rounded-lg">
                <p className="mb-1 text-xs text-gray-600">Daily Average</p>
                <p className="text-lg font-bold text-blue-600">
                  ₵{Math.round(weeklyData.length > 0 ? weeklyData.reduce((sum, day) => sum + day.earnings, 0) / 7 : 0).toLocaleString()}
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Payout Due Section */}
        {/* <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm"
        >
          <div className="flex flex-col mb-6 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="mb-1 text-lg font-semibold text-gray-900">Payout Due This Month</h3>
              <p className="text-sm text-gray-600">
                Commission payments awaiting processing
              </p>
            </div>
            
            <div className="flex items-center mt-4 space-x-4 sm:mt-0">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-yellow-500 rounded-full" />
                <span className="text-sm text-gray-600">{pendingPayouts.length} Pending</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-green-500 rounded-full" />
                <span className="text-sm text-gray-600">{processedPayouts.length} Processed</span>
              </div>
            </div>
          </div>

          {pendingPayouts.length > 0 ? (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {pendingPayouts.map((payout, index) => (
                <motion.div
                  key={payout.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <PayoutCard payout={payout} />
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="py-12 text-center">
              <CheckCircle className="mx-auto mb-4 w-16 h-16 text-green-500" />
              <h4 className="mb-2 text-lg font-medium text-gray-900">All Caught Up!</h4>
              <p className="text-gray-600">No pending payouts this month</p>
            </div>
          )}
        </motion.div> */}
      </div>
    </div>
  );
}
